import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import { Separator } from '@radix-ui/react-separator';
import {
  Button,
  Icon,
  Input,
  Logo,
  MenuContent,
  Progress,
  Skeleton,
} from 'next/components/ui';
import { easing } from 'next/components/ui/lib';
import { BeaconLink } from 'next/components/HelpScout';
import MagicIcon from './MagicIcon';

const SMALL_ICONS = new Set(['comment-alt']);

/**
 * Navigation components
 */
export const Wrapper = styled.div`
  background-color: var(--white);
  border-right: 1px solid var(--nav-border);
  bottom: 0;
  display: flex;
  flex-direction: column;
  left: 0;
  position: fixed;
  top: 0;
  width: var(--nav-width);
  z-index: 9999;
`;

export const Header = styled.header`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 20px 0;

  ${Logo} {
    width: 20px;
  }
`;

// TODO: Consider using @radix-ui/react-scroll-area
export const Scrollable = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: auto;
`;

export const Links = styled.nav`
  flex-grow: 1;
`;

export const Group = styled.ul`
  padding: 12px 0;
`;

export const Divider = styled(Separator)`
  background: var(--ui-horizontal-separator);
  height: 1px;
`;

/**
 * Navigation CTA components
 */
export const InstallWrapper = styled(Button)`
  align-items: center;
  background: var(--nav-install-bg);
  color: var(--nav-install-color);
  display: flex;
  font-size: var(--regular);
  font-weight: var(--bold);
  justify-content: center;
  padding: 8px;

  ${MagicIcon} {
    color: var(--nav-install-icon-color);
    margin-right: 8px;
    transition: ${easing('color')};
  }

  :hover {
    background: var(--nav-install-hover);

    ${MagicIcon} {
      color: var(--nav-install-icon-hover);
    }
  }
`;

export const TrialWrapper = styled.div`
  text-align: center;

  ${Skeleton} {
    width: 140px;
    height: 40px;
    margin: 0 auto;
  }

  ${Progress} {
    height: 12px;
    margin-bottom: 10px;
  }

  ${Button} {
    --link-color: var(--white);
    --link-text-decoration: none;
    --link-visited-color: var(--white);

    margin: 0 12px;
  }
`;

export const Counter = styled.div`
  font-weight: var(--bold);
  color: var(--heading-primary);
  font-size: var(--regular);
`;

export const TrialMessage = styled.div`
  color: var(--heading-primary);
  font-size: var(--regular);
  margin-bottom: 10px;
`;

export const CallToActions = styled.div`
  display: flex;
  flex-direction: column;

  > ${InstallWrapper}, > ${TrialWrapper} {
    margin: 0 24px 24px;
  }
`;

/**
 * Navigation Item components
 */
export const NavItemWrapper = styled.li`
  height: 40px;
`;

export const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-right: 12px;
  width: 20px;
`;

export const ItemIcon = styled(Icon)`
  color: var(--text-secondary);
  font-size: var(--regular);

  ${({ icon }) =>
    SMALL_ICONS.has(icon) &&
    `
      font-size: var(--small) ;
    `}

  ${({ icon }) =>
    icon === 'thumbtack' &&
    `
      transform: rotate(45deg);
    `}
`;

export const ItemLink = styled(NavLink)`
  align-items: center;
  color: var(--heading-primary);
  display: flex;
  font-size: var(--regular);
  font-weight: var(--normal);
  height: 100%;
  padding-left: 36px;
  text-decoration: none;

  &.active {
    background-color: var(--nav-item-active);
    font-weight: var(--bold);

    ${ItemIcon} {
      color: var(--primary);
    }
  }

  &:hover:not(.active),
  &:focus-visible {
    color: var(--primary);

    ${ItemIcon} {
      color: var(--primary);
    }
  }
`;

export const HelpScoutLink = styled(BeaconLink)`
  align-items: center;
  color: var(--disabled);
  display: flex;
  height: 100%;
  padding-left: 36px;

  &:hover,
  ${ItemIcon} {
    color: var(--disabled);
  }
`;

/**
 * Account Menu components
 */
export const AccountMenuContent = styled(MenuContent)`
  width: 235px;
`;

export const GearIcon = styled(Icon)`
  color: var(--dark-resting);
  font-size: var(--medium-large);
`;

export const CaretIcon = styled(Icon)`
  color: var(--dark-resting);
  font-size: var(--xx-small);
`;

export const AccountMenuTrigger = styled.div`
  padding: 16px 24px 16px 36px;

  &:hover {
    ${GearIcon} {
      color: var(--blurple);
    }
  }

  ${CaretIcon} {
    margin-right: 0;
  }

  ${({ open }) =>
    open &&
    `
      ${GearIcon} {
        color: var(--blurple);
      }
    `}
`;

/**
 * Spoof Control components
 */
export const SpoofControlWrapper = styled.form`
  display: flex;
  padding: 12px 16px 0;
  position: relative;

  ${Button} {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
    width: 76px;
  }

  ${Input} {
    border-bottom-right-radius: 0;
    border-color: var(--text-tertiary);
    border-top-right-radius: 0;
    width: 100%;
  }
`;
